/**
 * StarRating: nota de 1 a 5 estrelas (Péssimo a Excelente), acessível como grupo de rádio:
 * setas mudam a nota, clicar de novo na estrela escolhida limpa (a pesquisa é opcional).
 */
import { useRef } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { QUALITY_LABELS } from './videoCallCopy';

/**
 * @param {{ label: string, value: number|null, onChange: (value: number|null) => void, disabled?: boolean }} props
 */
export function StarRating({ label, value, onChange, disabled = false }) {
    const refs = useRef([]);

    const select = (rating) => {
        onChange(rating);
        refs.current[rating - 1]?.focus();
    };

    const onKeyDown = (event) => {
        const current = value ?? 0;
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault();
            select(Math.min(5, current + 1));
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault();
            select(Math.max(1, current - 1));
        }
    };

    return (
        <div className="flex flex-col items-center gap-1">
            <p className="text-sm text-foreground">{label}</p>
            <div role="radiogroup" aria-label={label} className="flex items-center gap-1" onKeyDown={onKeyDown}>
                {QUALITY_LABELS.map((text, index) => {
                    const rating = index + 1;
                    const filled = value != null && rating <= value;
                    const checked = value === rating;
                    return (
                        <button
                            key={rating}
                            ref={(el) => { refs.current[index] = el; }}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            aria-label={`${rating} de 5: ${text}`}
                            title={text}
                            disabled={disabled}
                            tabIndex={checked || (value == null && rating === 1) ? 0 : -1}
                            onClick={() => onChange(checked ? null : rating)}
                            className="rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
                        >
                            <Star
                                aria-hidden="true"
                                className={cn('h-6 w-6', filled ? 'fill-amber-400 text-amber-400' : 'text-foreground-muted')}
                            />
                        </button>
                    );
                })}
            </div>
            <p className="h-4 text-xs text-foreground-muted" aria-live="polite">
                {value != null ? QUALITY_LABELS[value - 1] : ''}
            </p>
        </div>
    );
}

export default StarRating;
